/**
 * Combine the WORKFLOW listener and the listener for every other status back
 * into the one list the whole-collection listener used to deliver.
 *
 * A status change reaches the two listeners separately, so for a moment the
 * same document can be in both: the listener that just fired has the newer
 * copy. The result is in document id order (plain code unit comparison, as
 * Firestore orders ids), which is the order the single unfiltered query had.
 */
export const mergeAppointmentSnapshots = (freshDocs, otherDocs) => {
  const byId = new Map(otherDocs.map((ca) => [ca.id, ca]));
  freshDocs.forEach((ca) => byId.set(ca.id, ca));
  return Array.from(byId.values()).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
};

/**
 * This listener's documents, plus any it held before that the other listener
 * does not have. Used while the other listener is not live (not loaded yet,
 * or in retry backoff): a status change that moves a document across cannot
 * be told from a deletion until the other side reports, so the document
 * stays where it was - stale, as with the single listener - instead of
 * vanishing from every tab.
 */
export const holdDepartedAppointments = (previousDocs, nextDocs, otherDocs) => {
  const presentIds = new Set([...nextDocs, ...otherDocs].map((ca) => ca.id));
  return [...nextDocs, ...previousDocs.filter((ca) => !presentIds.has(ca.id))];
};

/**
 * What each listener shows, from what each last received.
 *
 * A status change moves a document from one listener to the other, and while
 * either is not live (a cached snapshot, an error, not loaded yet) the move
 * cannot be told from a deletion. So until both are live, a document that
 * left one side and is not on the other stays where it was - stale, as with
 * the single listener - rather than vanishing from every tab. Once both are
 * live, each side is exactly what the server sent. A side that has received
 * nothing yet stays null.
 *
 * The other listener only starts once a screen needs it. Before that there is
 * nothing for a moved document to wait for, so WORKFLOW is exactly what the
 * server sent. A cached snapshot is still held as above: after a listen error
 * the retry can come back offline with an empty cache, which is not the
 * server saying the tab is empty.
 */
export const reconcileListenerAppointments = ({
  workflowReceived,
  otherReceived,
  workflowShown,
  otherShown,
  areBothLive,
  isOtherListenerStarted = true,
  isWorkflowListenerLive = false,
}) => {
  if (!isOtherListenerStarted) {
    if (!workflowReceived) return { workflow: workflowShown, other: null };
    const workflow = isWorkflowListenerLive
      ? workflowReceived
      : holdDepartedAppointments(workflowShown || [], workflowReceived, []);
    return { workflow, other: null };
  }
  if (areBothLive) {
    return { workflow: workflowReceived, other: otherReceived };
  }
  const workflow = workflowReceived
    ? holdDepartedAppointments(workflowShown || [], workflowReceived, otherShown || [])
    : workflowShown;
  const other = otherReceived ? holdDepartedAppointments(otherShown || [], otherReceived, workflow || []) : otherShown;
  return { workflow, other };
};

/**
 * Whether ARCHIVED and HISTORY can stop showing they are loading. Both sides
 * must have something, and the other listener must have heard from the
 * server: offline, its first snapshot comes from the cache, which is empty
 * for a query nobody has run yet, and would show as "no workflows".
 */
export const areArchivedAndHistoryLoaded = ({ workflowShown, otherShown, hasOtherServerSnapshot }) =>
  Boolean(workflowShown && otherShown && hasOtherServerSnapshot);
